import React, { useState, useEffect } from 'react';
import {connect} from "react-redux";
import {
	AUTHENTICATION,
	AUTHENTICATION_KEY,
	ID_NOT_SET,
	ECOMMERCE_INAPP_LOGIN,
	JWT_LOGIN,
	GLOBALS_KEY,
	SUBSCRIBER,
	REFERRER_LOGIN,
	REFERRER_LOGIN_LI,
	SUBSCRIBER_LOGIN,
	SUBSCRIBER_LOGIN_LI
} from "../../_MODULE_GLOBALS/constants";
import {getStoreValue} from "../../../utils/storeValue";
import {displayOnDevice} from "../../../utils/moduleSetup";
import * as Tracking from "../../../utils/tracking";
import {getActiveAttributeState} from "../../../utils/moduleSetup";
import {isEmpty, isTrue} from "../../../utils/generalUtils";
import {clone} from "../../../utils/objectUtils";
import {AuthenticatePrompt} from "./widgets/prompt";
import {AuthenticateLogin} from "./widgets/login";
import {useFetchAttributesChange, useFetchComplete} from "../../../hooks/useFetchChange";
import {AuthenticateApp} from "./widgets/app";
import {AuthenticateEcommerce} from "./widgets/ecommerce";
import {useAttributesChanged} from "../../../hooks/useAttributesChanged";
import {checkIfMyPane} from "../../globals/globalLibraryData";
import {setCookie, getCookie} from "../../../utils/standardizedCookies";
import {COOKIE_NAMES} from "../../../utils/standardizedCookies";
import {AccessPending} from "./widgets/accessPending";



/**
 * Returns a div with message (usually error) or empty, when no message to display.
 * Called from an Authentication widget, but can be seperated/exported
 * and called from anywhere if we have more areas where we'd need
 * to display messages.
 *
 * @param params
 *     message: the message to display
 *     className: to apply to the message div
 *         usually "error" or "information"
 *     clearErrorMsg: true: clear current message; false: use existing
 * @returns {JSX.Element|string}
 * @constructor
 */
const AuthenticationMsg = (params) => {
	params = Object.assign({
		message: '',
		className: 'error',
		clearErrorMsg: false,
	}, params);

	const authenticationMsg = isTrue(params.clearErrorMsg, {defaultValue: false}) ? '' : (!isEmpty(params.message) ? params.message : '');
	const className = !isEmpty(params.className) ? params.className : 'error';

	if (!isEmpty(authenticationMsg)) {
		return (
			<p className={className}>{authenticationMsg}</p>
		);
	} else {
		return '';
	}
};
export {AuthenticationMsg};

/**
 * Call tracking on buton clicck passing widget-specific tracking properties.
 *
 * @param trackingProperties properties to pass to tracking
 */
const trackButtonClick = (trackingProperties) => {
	Tracking.libraryTrack("button clicked", trackingProperties);
};
export {trackButtonClick};


/**
 *
 * @param props - Props needed for the Authentication module
 * @returns {JSX.Element|string}
 * @constructor
 */
const AuthenticationModule = (props) => {
	const [authenticationInProgress, setAuthenticationInProgress] = useState(false);
	const [userAccess, setUserAccess] = useState(props.userAccess);
	const [authenticationDialogShown, setAuthenticationDialogShown] = useState(false);

	// Keep local userAccess in sync with props when props.userAccess changes
	useEffect(() => {
		if (!isEmpty(props.userAccess)) {
			setUserAccess(props.userAccess);
		}
	}, [props.userAccess]);

	const isEcommerce = props.authenticationType === ECOMMERCE_INAPP_LOGIN;
	const isJWT = props.authenticationType === JWT_LOGIN;
	const isSubmanaged = (!props.app && props.authenticationType === SUBSCRIBER_LOGIN);
	const isLookinside = (!props.app && (props.authenticationType === SUBSCRIBER_LOGIN_LI || props.authenticationType === REFERRER_LOGIN_LI));

	/**
	 * For ecommerce apps, once a user goes through the subscription or single-issue purchase 
	 * the userAccess object in this module doesn't get updated immediatelly which would cause the popup to remain in place.
	 * To work around this, we are comparing the purchaseProductId to productId of the products displayed in this popup.
	 * If one of them matches and we have a successful purchase we hide the popup.
	 */
	const isCurrentIssuePurchaseSuccessful = props.isPurchaseSuccessful && 
		(props.singleIssueProduct.productId === props.purchaseProductId ||
		props.subscriptionProduct.productId === props.purchaseProductId);

	const currentFolio = props.currentFolio !== ID_NOT_SET ? props.currentFolio : "";
	const currentPageNumber = (typeof props.currentPageNumber !== "undefined" && props.currentPageNumber !== ID_NOT_SET) ? props.currentPageNumber : undefined;
	let isArticleViewer = true;//default
	if (props.currentFolio === ID_NOT_SET && props.currentPageNumber === ID_NOT_SET && props.currentArticleId === ID_NOT_SET) {
		// unknown, going with defalt;
	} else {
		isArticleViewer = props.currentFolio === ID_NOT_SET && props.currentPageNumber === ID_NOT_SET && props.currentArticleId !== ID_NOT_SET;
	}
	const isCurrentArticleOpen = props.isCurrentArticleOpen;
	const isOpen = (props.authenticationType !== REFERRER_LOGIN && props.authenticationType !== SUBSCRIBER_LOGIN 
		&& props.authenticationType !== SUBSCRIBER_LOGIN_LI && props.authenticationType !== REFERRER_LOGIN_LI && !isEcommerce && !isJWT);
	const isLookinsideSubscriber = !isEmpty(props.subscriberId) && !isEmpty(props.lookinsideIds)
		&& Object.values(props.lookinsideIds).includes(props.subscriberId);
	const isLoggedInAsSubscriber = isTrue(props.isLoggedIn) && ((!isEmpty(props.subscriberId) || !isEmpty(props.lookinsideIds)) 
		&& !isLookinsideSubscriber);
	const accessPages = userAccess.accessPages[props.issueUrl];
	let userHasAccess = false;
	if (isArticleViewer) {
		userHasAccess = isTrue(userAccess.access[props.issueUrl] && !isLookinsideSubscriber && isEmpty(accessPages), {defaultValue: false});
	} else {
		userHasAccess = isTrue(userAccess.access[props.issueUrl], {defaultValue: true});
	}
	const userHasAccessToPage = isEmpty(accessPages) || currentPageNumber === ID_NOT_SET || 
		accessPages.includes(String(currentFolio));
	if (isLookinside && !isArticleViewer) {
		userHasAccess = userHasAccess && userHasAccessToPage;
	}

	const blockLookinsideForArticleViewer = isArticleViewer && !isEmpty(props.loginId);
	const missingLookinsideIdForIssue = isEmpty(props.lookinsideIds) || isEmpty(props.lookinsideIds[props.issueUrl]);
	// The lookinside call needs a resolved folio (the backend anchors the page-limited preview window
	// to it). In the article viewer there is no folio (globals.currentFolio is ID_NOT_SET), so both the
	// fetch AND the "configuring access" pending state must wait for a folio — otherwise AccessPending
	// shows forever in the article viewer while the gated fetch never runs. Navigating into the replica
	// viewer seeds the folio, which re-triggers both.
	const hasResolvedFolio = !isEmpty(currentFolio);
	const lookinsideFetchNeeded = isLookinside && props.issueUrl && !blockLookinsideForArticleViewer && hasResolvedFolio
		&& (isArticleViewer ? !props.userAccess.access[props.issueUrl] : missingLookinsideIdForIssue);

	const popupType = isEcommerce && props.authView !== 'login' ? 'ecommerce' : !isEcommerce && props.app === true ? 'app' : props.authView;
	const dontShowAuthentication = !displayOnDevice(props) || userHasAccess || isOpen || isCurrentIssuePurchaseSuccessful;

	const baseTrackingProperties = {
		"module": "authentication",
		"issue name": "_" + props.issueName,
		"issue url": props.issueUrl,
		"subscriber status": props.isLoggedIn ? "subscriber" : (props.failedLoginAttempt ? "login failed" : "lookinside"),
	};


	/**
	 * Called to manage whether or not the module fetches data.  If the hook calls the
	 * callback, then call getReplicaList which will determine whether or not to
	 * fetch a new list or get an existing list from stored data.
	 *
	 */
	useFetchAttributesChange((params) => {
		const fetchData = getStoreValue({attributeKey: 'genericFetchData'});
		setAuthenticationInProgress(true);
		fetchData({type: AUTHENTICATION, storageKey: props.storageKey});
	}, {props: props, additionalTriggers: {isLoggedIn: props.isLoggedIn}});

	/**
	 * Call hook to check to see if the authenticationInProgress has been set/changed in the reducer.
	 * authenticationInProgress is set true when fetch is started (DATA_REQUESTED)
	 * and set to false when fetch is done (DATA_LOADED)
	 */
	useFetchComplete((isInProgress) => {
		setAuthenticationInProgress(isTrue(isInProgress, {defaultValue: false}));
		const userAccess = !isEmpty(props.userAccess) ? props.userAccess : props.defaultUserAccess;
		setUserAccess(userAccess);
		if (!isInProgress && userAccess.access[props.issueUrl]) {
			const trackingProperties = {
				"isSubmanaged": isSubmanaged ,
				"userHasAccess": userHasAccess,
				"isLoggedInAsSubscriber": isTrue(props.isLoggedIn) && ((!isEmpty(props.subscriberId) || !isEmpty(props.lookinsideIds)) && isLookinsideSubscriber),
				"popupType": popupType,
				"type": "login",
				"subscriber status": props.isLoggedIn ? "subscriber" : "lookinside",
				"action": "logged in"
			};
			Tracking.libraryTrack("login popup action", trackingProperties);
		}
	}, {requestInProgress: props.authenticationInProgress, fetchInProgress: authenticationInProgress});

	/**
	 * Check if successful login response from server call.
	 * If true, set the subscriber_id cookie
	 * if false,
	 */
	useAttributesChanged((params) => {
		// setup to trigger/update login failed/succeeded
		if (props.isMyPane) {
			if (!isEmpty(props.subscriberId)) {  // successful login
				const cookieOpts = {path: "/" + props.collectionUrl, secure: props.secureCookie, sameSite: props.secureCookie ? "None" : "Lax"};
				setCookie(null, COOKIE_NAMES.SUBSCRIBER_ID, props.subscriberId, cookieOpts);
				// Set login_id as backup only for non-lookinside (case 2)
				if (!isLookinside) {
					setCookie(null, COOKIE_NAMES.LOGIN_ID, props.subscriberId, cookieOpts);
				}
			} else if (!isEmpty(props.emailAddress) && isTrue(props.failedLoginAttempt)) {  // login attempted by invalid email
				const event = new CustomEvent("gtxlogin", {detail: {category: "login popup action", "subscriber status": "login failed"}});
				document.dispatchEvent(event);
			}
		}
	}, [props.emailAddress, props.subscriberId, props.failedLoginAttempt]);

	useAttributesChanged((params) => {
		// setup lookinside
		if (props.isMyPane) {
			if (isLookinside && !isEmpty(props.issueUrl) && !isEmpty(props.lookinsideIds)) {
				const existingLookinsideIds = getCookie(null, COOKIE_NAMES.LOOKINSIDE_IDS);
				const issUrl = clone(props.issueUrl);
				if (isEmpty(existingLookinsideIds) || isEmpty(existingLookinsideIds[issUrl])) {
					const lookinsideIdsValue = !isEmpty(existingLookinsideIds)
						? { ...existingLookinsideIds, ...props.lookinsideIds }
						: props.lookinsideIds;
					setCookie(null, COOKIE_NAMES.LOOKINSIDE_IDS, lookinsideIdsValue, { path: "/" + props.collectionUrl, secure: props.secureCookie, sameSite: props.secureCookie ? "None" : "Lax" });
				}
			}
		}
	}, [props.subscriberId, props.lookinsideIds, props.isLookinside, props.issueUrl]);

	// For lookinside subscribers: after SUBSCRIBER login succeeds (isLoggedIn becomes true),
	// or when a new lookinsideId is obtained, trigger the AUTHENTICATION fetch to update userAccess
	useAttributesChanged(() => {
		if (isLookinside && props.isLoggedIn) {
			// Pass issueUrl when lookinsideIds exist so server uses correct subscriberId per issue
			// Use the latest issueUrl from lookinsideIds (not props.issueUrl which might be stale)
			let queryParams = {};
			let targetIssueUrl = props.issueUrl;
			if (!isEmpty(props.lookinsideIds)) {
				const lookinsideKeys = Object.keys(props.lookinsideIds);
				const latestIssueUrl = lookinsideKeys[lookinsideKeys.length - 1];
				targetIssueUrl = latestIssueUrl;
				queryParams = { issueUrl: latestIssueUrl };
			}
			// Only refresh access when we don't already have it for the target issue.
			// subscriberId is in the trigger deps and changes on every issue switch (globals.subscriberId
			// tracks the active issue), so without this guard, navigating back to an already-authorized
			// issue re-fires a redundant /access fetch. When access is already granted, there's nothing
			// to refresh — the newly-obtained-lookinsideId case (the reason this effect exists) still
			// fires because access for that issue is not yet true.
			const alreadyHasAccess = !isEmpty(props.userAccess) && !isEmpty(props.userAccess.access)
				&& isTrue(props.userAccess.access[targetIssueUrl], {defaultValue: false});
			if (!alreadyHasAccess) {
				const fetchData = getStoreValue({attributeKey: 'genericFetchData'});
				fetchData({type: AUTHENTICATION, storageKey: props.storageKey, queryParams: queryParams});
			}
		}
	}, [props.isLoggedIn, props.subscriberId, props.lookinsideIds]);

	/**
	 * For lookinside users who don't yet have access to the current issue and no
	 * authentication is in progress, trigger the auto-login fetch via SUBSCRIBER.
	 * This replaces the inline fetch that previously ran directly during render.
	 */
	useFetchAttributesChange((params) => {
		// lookinsideFetchNeeded already requires a resolved folio (hasResolvedFolio), so in the article
		// viewer (no folio) this stays false and the fetch waits until the replica navigation seeds the
		// folio — keeping the fetch and the AccessPending pending state in lockstep.
		if (lookinsideFetchNeeded && !authenticationInProgress) {
			const fetchData = getStoreValue({attributeKey: 'genericFetchData'});
			setAuthenticationInProgress(true);
			const queryParams = { "folio": currentFolio, "pg": currentPageNumber, "mode": "lookinside", "issueUrl": props.issueUrl };
			fetchData({type: SUBSCRIBER, storageKey: GLOBALS_KEY, queryParams: queryParams});
		}
	}, {props: props, queryParams: {isLoggedIn: props.userAccess.isLoggedIn}, additionalTriggers: {isLookinside, issueUrl: props.issueUrl, currentFolio, currentPageNumber, isLoggedIn: props.isLoggedIn}});

	const additionalProps = {
		"isSubmanaged": isSubmanaged || (isLookinside && !isEmpty(props.loginId)),
		"userHasAccess": userHasAccess,
		"userHasAccessToPage": userHasAccessToPage,
		"accessPages": accessPages,
		"isLoggedInAsSubscriber": isTrue(props.isLoggedIn) && ((!isEmpty(props.subscriberId) || !isEmpty(props.lookinsideIds)) && !isLookinsideSubscriber),
		"popupType": popupType,
	};


	if (dontShowAuthentication) {
		// if user has access to this issue (open or logged in), don't show auth popup
		return null;
	} else if (authenticationInProgress && props.isLoggedIn && isLookinside && userHasAccess) {
		// For lookinside with access, don't show AccessPending — content will load
		return null;
	} else if (authenticationInProgress && props.isLoggedIn) {
		// While authentication fetch is in progress for a logged-in user, show pending
		// state to prevent the login popup from flashing before userAccess updates.
		return <AccessPending {...props} {...additionalProps} />;
	} else if (lookinsideFetchNeeded && !authenticationInProgress) {
		return <AccessPending {...props} {...additionalProps} />;
	} else {
		// otherwise, continue normal display of authentication dialog
		if (!isTrue(authenticationDialogShown)) {
			// state to trigger tracking of authentication dialog shown only once
			setAuthenticationDialogShown(true);
		}
		if (popupType === 'app') {
			return <AuthenticateApp {...props} {...additionalProps} />;
		} else if (popupType === 'ecommerce') {
			return <AuthenticateEcommerce {...props} {...additionalProps} />;
		} else if (popupType === 'prompt' && (!isLoggedInAsSubscriber || (isLookinside && isEmpty(props.loginId)))) {
			return <AuthenticatePrompt {...props} {...additionalProps} />;
		} else {  // "login"
			return <AuthenticateLogin {...props} {...additionalProps} />;
		}
	}
};



/**
 * Map state (store) data.
 *
 * storageKey provides a way to map a specific store object to a particular
 * instance of the module.  Add storageKey when configuring the module instance
 * if it needs to listen for a store change.
 *
 * @param state store state
 * @param props module props, passed through action to store and back
 * @returns {*}
 */
const mapStateToProps = (state, props) => {
	const storageKey = !isEmpty(props.storageKey) ? props.storageKey : AUTHENTICATION_KEY;
	const storeState = !isEmpty(state[storageKey]) ? state[storageKey] : {};
	const globalState = !isEmpty(state.globals) ? state.globals : {};

	const urlNavigationTarget = !isEmpty(state.urlNavigationTarget) ? state.urlNavigationTarget : {};

	// currentIssueUrl is only written by shareUrl.js (not by replicaViewer on initial navigation),
	// so fall back to urlNavigationTarget.issueUrl which is set from the URL on page load.
	const issueUrl = !isEmpty(globalState.currentIssueUrl)
		? globalState.currentIssueUrl
		: (!isEmpty(urlNavigationTarget.issueUrl) ? urlNavigationTarget.issueUrl : '');

	const documentList = !isEmpty(state.documentList) ? state.documentList : [];
	let document = documentList.find(document => document.issueUrl === issueUrl);
	document = !isEmpty(document) ? document : {};
	const documentUrl = !isEmpty(document.documentUrl) ? document.documentUrl : '';
	const documentId = !isEmpty(document.documentId) ? document.documentId : '';

	const myPane = checkIfMyPane({moduleName: 'authentication', instanceId: props.instanceId, storageKey: storageKey});

	const products = !isEmpty(globalState.products) ? globalState.products : [];
	const subscriptionProduct = products.find(product => product.productType === "subscription");
	const singleIssueProduct = products.find(product => product.productType === "single_issue" && documentUrl.includes(product.documentUrl));

	const userAccess = storeState.userAccess;
	// Use urlNavigationTarget.folio as fallback when currentFolio is not yet set (e.g. SUBSCRIBER_LOGIN_LI on initial page load)
	const currentFolio = globalState.currentFolio !== ID_NOT_SET
		? globalState.currentFolio
		: (!isEmpty(urlNavigationTarget.folio) ? urlNavigationTarget.folio : globalState.currentFolio);
	const currentPageNumber = globalState.currentPageNumber !== ID_NOT_SET
		? globalState.currentPageNumber
		: (!isEmpty(urlNavigationTarget.pageNumber) ? urlNavigationTarget.pageNumber : globalState.currentPageNumber);
	const authenticationProps = {
		// Only used to generate other properties
//		"documentList": documentList,
//		"documentUrl": documentUrl,
//		"documentId": documentId,
//		"platform": !isEmpty(state.platform) ? state.platform : "web",
//		"products": products,
//		"isHapara": !!(state.collectionSubscribeUrl && state.collectionSubscribeUrl.toLowerCase().includes("hapara")),

		// Only used by articleViewer and replicaViewer
//		"purchaseProductId": !isEmpty(globalState.purchaseProductId) ? globalState.purchaseProductId : "",


		// GENERIC MODULE PROPS
		"active": getActiveAttributeState(props),
		"module": "authentication",
		"isMyPane": myPane.isMyPane,
		"myPane": myPane.myPane,
		"authenticationInProgress": isTrue(storeState.authenticationInProgress, {defaultValue: false}),
		"subscriberInProgress": isTrue(globalState.subscriberInProgress, {defaultValue: false}),

		// AUTHENTICATION MODULE ONLY
		"defaultUserAccess": !isEmpty(state.userAccess) ? state.userAccess : {
			isLoggedIn: false,
			access: {},
			accessPages: {}
		},
		"currentFolio": currentFolio,
		"currentPageNumber": currentPageNumber,
		"currentArticleId": globalState.currentArticleId !== ID_NOT_SET
			? globalState.currentArticleId
			: (!isEmpty(urlNavigationTarget.articleId) ? urlNavigationTarget.articleId : globalState.currentArticleId),
		"userAccess": !isEmpty(storeState.userAccess) ?
			storeState.userAccess :
			(!isEmpty(state.userAccess) ?
				state.userAccess :
				{
					isLoggedIn: false,
					access: {},
					accessPages: {}
				}),
		//"userHasAccessToPage": userHasAccessToPage,
		"subscriberId": !isEmpty(globalState.subscriberId) ? globalState.subscriberId : '',
		"loginId": !isEmpty(globalState.loginId) ? globalState.loginId : null,
		"lookinsideIds": !isEmpty(globalState.lookinsideIds)
			? globalState.lookinsideIds
			: (() => {
				const ids = getCookie(null, COOKIE_NAMES.LOOKINSIDE_IDS);
				return !isEmpty(ids) ? ids : null;
			})(),
		"isLookinsideSubscriber": !isEmpty(globalState.isLookinsideSubscriber) ? globalState.isLookinsideSubscriber : null,
		"failedLoginAttempt": isTrue(globalState.failedLoginAttempt, {defaultValue: false}),
		"secureCookie": (typeof globalState.secureCookie !== 'undefined') ? isTrue(globalState.secureCookie) : true,
		"collectionUrl":  !isEmpty(state.collectionUrl) ? state.collectionUrl : '',
		"machineLimitReached": isTrue(globalState.machineLimitReached, {defaultValue: false}),

		// ALL WIDGETS
		"authView": !isEmpty(storeState.authView) ? storeState.authView : 'prompt',
		"issueUrl": issueUrl,
		"issueName": !isEmpty(documentList[issueUrl]) ? "_" + documentList[issueUrl] : "_" + issueUrl,
		"authenticationType": !isEmpty(state.authenticationType) ? state.authenticationType : '',
		"collectionSubscribeUrl": !isEmpty(state.collectionSubscribeUrl) ? state.collectionSubscribeUrl : '',
		"subscribeUrl": !isEmpty(state.subscribeUrl) ? state.subscribeUrl : '',

		// LOGIN/PROMPT WIDGET
		"isCurrentArticleOpen": isTrue(globalState.currentArticleIsOpen, {defaultValue: false}),
		"isPurchaseSuccessful": isTrue(globalState.isPurchaseSuccessful, {defaultValue: false}),
		"purchaseProductId": !isEmpty(globalState.purchaseProductId) ? globalState.purchaseProductId : "",

		// ECOMMERCE WIDGET
		"isLatestIssue" : !isEmpty(state.latestIssue) ? state.latestIssue.issueUrl === issueUrl : false,
		"subscriptionProduct": !isEmpty(subscriptionProduct) ? subscriptionProduct : {},
		"singleIssueProduct": !isEmpty(singleIssueProduct) ? singleIssueProduct : {},

		// AUTHENTICATION/LOGIN/ECOMMERCE
		"isLoggedIn": isTrue(globalState.isLoggedIn, {defaultValue: false}),
		"accessError": !isEmpty(globalState.accessError) ? globalState.accessError : '',
		"clearErrorMsg": isTrue(storeState.clearErrorMsg, {defaultValue: false}),
		"emailAddress": !isEmpty(storeState.emailAddress) ? storeState.emailAddress : '',
		"malformedEmail": isTrue(storeState.malformedEmail, {defaultValue: false}),

		// APP/ECOMMERCE
		"resourceId": !isEmpty(documentList.resourceId) ? documentList.resourceId : '',
		"successUrl": !isEmpty(state.successUrl) ? state.successUrl : '',
		"expirationDays": !isEmpty(state.expirationDays) ? state.expirationDays : '',

		// APP/LOGIN
		"app": isTrue(state.app, {defaultValue: false}),

	};

	return authenticationProps;
};

/**
 * simpleImage module (currently) makes to calls to fetch of update data.
 *
 * @param dispatch call action
 * @returns {{updateData: updateData}}
 */
function mapDispatchToProps(dispatch) {
	return {
	};
}

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(AuthenticationModule);

